import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStaffDashboard, getMyDashboardSummary } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  BookOpen, 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  XCircle,
  ChevronRight,
  Loader2,
  LogOut,
  User,
  LayoutDashboard,
  Sprout,
  ClipboardList,
  Shield,
  PenTool,
  Calendar,
  DollarSign,
  FileText
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const statusConfig = {
  completed: {
    label: 'Completed',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    icon: CheckCircle,
    iconColor: 'text-emerald-600'
  },
  in_progress: {
    label: 'In Progress',
    color: 'bg-sky-100 text-sky-700 border-sky-200',
    icon: Clock,
    iconColor: 'text-sky-600'
  },
  not_started: {
    label: 'Not Started',
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    icon: BookOpen,
    iconColor: 'text-gray-500'
  },
  expired: {
    label: 'Expired',
    color: 'bg-red-100 text-red-600 border-red-200',
    icon: XCircle,
    iconColor: 'text-red-500'
  },
  due_soon: {
    label: 'Due Soon',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: AlertTriangle,
    iconColor: 'text-amber-600'
  }
};

const StaffDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [schedulingSummary, setSchedulingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [response, schedRes] = await Promise.all([
          getStaffDashboard(),
          getMyDashboardSummary().catch(() => ({ data: null }))
        ]);
        setDashboard(response.data);
        setSchedulingSummary(schedRes.data);
      } catch (err) {
        setError('Failed to load dashboard');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FAF7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#5BA4A4]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FAF7]">
        <Card className="w-full max-w-md border-[#A8E6CF]/30">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <p className="text-[#5A7A7A]">{error}</p>
            <Button onClick={() => window.location.reload()} className="mt-4 bg-[#5BA4A4] hover:bg-[#4A9090]">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { stats, completion_percentage, modules, onboarding_complete } = dashboard;

  // Show onboarding lock for non-admin staff who haven't completed onboarding
  if (!onboarding_complete && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F0FAF7] to-white">
        {/* Header */}
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#A8E6CF]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-xl flex items-center justify-center shadow-sm">
                  <Sprout className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-sm sm:text-lg font-bold text-[#2D4A4A]">Growing Minds</h1>
                  <p className="text-[10px] sm:text-xs text-[#5BA4A4] hidden sm:block">Training Portal</p>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="p-2 sm:px-3 border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]">
                    <User className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="border-[#A8E6CF]/30">
                  <DropdownMenuLabel className="text-[#2D4A4A]">My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-[#5A7A7A]">{user?.email}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="text-[#5BA4A4] cursor-pointer" data-testid="profile-link">
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-500">
                    <LogOut className="w-4 h-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12 text-center" data-testid="onboarding-lock-screen">
          <div className="w-20 h-20 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-[#2D4A4A] mb-3">Complete Onboarding First</h2>
          <p className="text-[#5A7A7A] mb-6 max-w-md mx-auto">
            Before you can access training modules, you need to complete your onboarding and be cleared by an administrator. This ensures all safer recruitment requirements are met.
          </p>
          <Link to="/onboarding">
            <Button size="lg" className="bg-[#5BA4A4] hover:bg-[#4A9090] text-white" data-testid="go-to-onboarding-btn">
              <ClipboardList className="w-5 h-5 mr-2" />
              Start Onboarding
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0FAF7] to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#A8E6CF]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-xl flex items-center justify-center shadow-sm">
                <Sprout className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h1 className="text-sm sm:text-lg font-bold text-[#2D4A4A]">Growing Minds</h1>
                <p className="text-[10px] sm:text-xs text-[#5BA4A4] hidden sm:block">Training Portal</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              {user?.role === 'admin' && (
                <Link to="/admin">
                  <Button variant="outline" size="sm" data-testid="admin-dashboard-link" className="hidden sm:flex border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Admin Dashboard
                  </Button>
                  <Button variant="outline" size="sm" data-testid="admin-dashboard-link-mobile" className="flex sm:hidden p-2 border-[#A8E6CF] text-[#5BA4A4]">
                    <LayoutDashboard className="w-4 h-4" />
                  </Button>
                </Link>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="user-menu-button" className="p-2 sm:px-3 border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]">
                    <User className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="border-[#A8E6CF]/30">
                  <DropdownMenuLabel className="text-[#2D4A4A]">My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-[#5A7A7A]">
                    {user?.email}
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-[#5A7A7A] capitalize">
                    Role: {user?.role}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="text-[#5BA4A4] cursor-pointer" data-testid="profile-link-main">
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contract')} className="text-[#5BA4A4] cursor-pointer" data-testid="contract-link">
                    <PenTool className="w-4 h-4 mr-2" /> My Contract
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-schedule')} className="text-[#5BA4A4] cursor-pointer" data-testid="schedule-link">
                    <Clock className="w-4 h-4 mr-2" /> My Schedule
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/available-shifts')} className="text-[#5BA4A4] cursor-pointer" data-testid="available-shifts-link">
                    <AlertTriangle className="w-4 h-4 mr-2" /> Available Shifts
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-timesheet')} className="text-[#5BA4A4] cursor-pointer" data-testid="timesheet-link">
                    <Clock className="w-4 h-4 mr-2" /> Timesheet & Pay
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-red-500">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Welcome & Stats */}
        <div className="mb-4 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-[#2D4A4A] mb-1 sm:mb-2">
            Welcome back, {user?.name}
          </h2>
          <p className="text-sm sm:text-base text-[#5A7A7A]">Track your mandatory training progress below</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4 mb-4 sm:mb-8">
          <Card className="border border-[#A8E6CF]/30 bg-white">
            <CardContent className="p-2 sm:pt-4 sm:pb-4 sm:px-4">
              <div className="text-lg sm:text-2xl font-bold text-[#2D4A4A]">{stats.total}</div>
              <div className="text-[10px] sm:text-sm text-[#5A7A7A]">Total</div>
            </CardContent>
          </Card>
          <Card className="border border-emerald-200 bg-emerald-50">
            <CardContent className="p-2 sm:pt-4 sm:pb-4 sm:px-4">
              <div className="text-lg sm:text-2xl font-bold text-emerald-700">{stats.completed}</div>
              <div className="text-[10px] sm:text-sm text-emerald-600">Done</div>
            </CardContent>
          </Card>
          <Card className="border border-sky-200 bg-sky-50">
            <CardContent className="p-2 sm:pt-4 sm:pb-4 sm:px-4">
              <div className="text-lg sm:text-2xl font-bold text-sky-700">{stats.in_progress}</div>
              <div className="text-[10px] sm:text-sm text-sky-600">Progress</div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200 hidden sm:block">
            <CardContent className="p-2 sm:pt-4 sm:pb-4 sm:px-4">
              <div className="text-lg sm:text-2xl font-bold text-gray-600">{stats.not_started}</div>
              <div className="text-[10px] sm:text-sm text-[#5A7A7A]">Not Started</div>
            </CardContent>
          </Card>
          <Card className="border border-amber-200 bg-amber-50 hidden sm:block">
            <CardContent className="p-2 sm:pt-4 sm:pb-4 sm:px-4">
              <div className="text-lg sm:text-2xl font-bold text-amber-700">{stats.due_soon}</div>
              <div className="text-[10px] sm:text-sm text-amber-600">Due Soon</div>
            </CardContent>
          </Card>
          <Card className="border border-red-200 bg-red-50 hidden sm:block">
            <CardContent className="p-2 sm:pt-4 sm:pb-4 sm:px-4">
              <div className="text-lg sm:text-2xl font-bold text-red-600">{stats.expired}</div>
              <div className="text-[10px] sm:text-sm text-red-500">Expired</div>
            </CardContent>
          </Card>
        </div>

        {/* Overall Progress */}
        <Card className="mb-4 sm:mb-8 border border-[#A8E6CF]/30 bg-white">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg text-[#2D4A4A]">Overall Compliance</CardTitle>
            <CardDescription className="text-xs sm:text-sm text-[#5A7A7A]">Your training completion progress</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex-1">
                <Progress value={completion_percentage} className="h-2 sm:h-3" />
              </div>
              <div className="text-xl sm:text-2xl font-bold text-[#5BA4A4]">
                {completion_percentage}%
              </div>
            </div>
            {completion_percentage < 100 && (
              <p className="text-xs sm:text-sm text-[#5A7A7A] mt-2">
                Complete all training to reach 100%
              </p>
            )}
          </CardContent>
        </Card>

        {/* Schedule & Pay Summary */}
        {schedulingSummary && (
          <Card className="mb-4 sm:mb-8 border border-[#A8E6CF]/30 bg-white" data-testid="scheduling-pay-summary">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#5BA4A4]" />
                  <h3 className="text-sm font-semibold text-[#2D4A4A]">Schedule & Pay</h3>
                </div>
                <div className="flex gap-1">
                  <Link to="/my-schedule">
                    <Button variant="ghost" size="sm" className="text-xs text-[#5BA4A4] hover:bg-[#F0FAF7]" data-testid="view-schedule-link">
                      Schedule <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                  <Link to="/my-timesheet">
                    <Button variant="ghost" size="sm" className="text-xs text-[#5BA4A4] hover:bg-[#F0FAF7]" data-testid="view-pay-link">
                      Pay <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* This Week */}
              {schedulingSummary.schedule ? (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="text-center p-2 rounded-lg bg-[#E8F8F5]">
                    <p className="text-lg font-bold text-[#5BA4A4]">{schedulingSummary.schedule.total_sessions}</p>
                    <p className="text-[10px] text-[#5A7A7A]">Sessions This Week</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-emerald-50">
                    <p className="text-lg font-bold text-emerald-700">{schedulingSummary.schedule.confirmed}</p>
                    <p className="text-[10px] text-emerald-600">Confirmed</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-amber-50">
                    <p className="text-lg font-bold text-amber-700">{schedulingSummary.schedule.pending}</p>
                    <p className="text-[10px] text-amber-600">Pending</p>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-[#5A7A7A] mb-3 p-2 rounded-lg bg-gray-50 text-center">No schedule assigned this week</p>
              )}

              {/* Next Session */}
              {schedulingSummary.next_session && (
                <div className="p-2.5 rounded-lg bg-sky-50 border border-sky-200 mb-3" data-testid="next-session-card">
                  <p className="text-xs font-semibold text-sky-700 mb-1 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> Next Session
                  </p>
                  <p className="text-sm font-medium text-sky-800 capitalize">
                    {schedulingSummary.next_session.day} — {schedulingSummary.next_session.start_time} to {schedulingSummary.next_session.end_time}
                  </p>
                  {schedulingSummary.next_session.subject && (
                    <p className="text-xs text-sky-600 mt-0.5">
                      {schedulingSummary.next_session.subject}{schedulingSummary.next_session.student_name ? ` with ${schedulingSummary.next_session.student_name}` : ''}
                    </p>
                  )}
                </div>
              )}

              {/* Pay Info */}
              <div className="grid grid-cols-2 gap-2">
                {schedulingSummary.timesheet ? (
                  <div className="text-center p-2 rounded-lg bg-[#E8F8F5]">
                    <p className="text-lg font-bold text-[#5BA4A4]">{schedulingSummary.timesheet.total_hours}h</p>
                    <p className="text-[10px] text-[#5A7A7A]">Hours This Month</p>
                  </div>
                ) : (
                  <div className="text-center p-2 rounded-lg bg-gray-50">
                    <p className="text-lg font-bold text-gray-400">—</p>
                    <p className="text-[10px] text-[#5A7A7A]">No Timesheet Yet</p>
                  </div>
                )}
                {schedulingSummary.invoice ? (
                  <div className="text-center p-2 rounded-lg bg-amber-50">
                    <p className="text-lg font-bold text-amber-700">£{schedulingSummary.invoice.total_amount.toFixed(2)}</p>
                    <p className="text-[10px] text-amber-600">Unpaid Invoice</p>
                  </div>
                ) : schedulingSummary.timesheet ? (
                  <div className="text-center p-2 rounded-lg bg-emerald-50">
                    <p className="text-lg font-bold text-emerald-700">£{schedulingSummary.timesheet.total_amount.toFixed(2)}</p>
                    <p className="text-[10px] text-emerald-600">Earned This Month</p>
                  </div>
                ) : (
                  <div className="text-center p-2 rounded-lg bg-gray-50">
                    <p className="text-lg font-bold text-gray-400">—</p>
                    <p className="text-[10px] text-[#5A7A7A]">No Pay Data</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alerts */}
        {(stats.expired > 0 || stats.due_soon > 0) && (
          <div className="mb-4 sm:mb-8 space-y-2 sm:space-y-3">
            {stats.expired > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 flex items-start sm:items-center gap-2 sm:gap-3">
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0 mt-0.5 sm:mt-0" />
                <div>
                  <p className="text-sm sm:text-base font-medium text-red-700">
                    {stats.expired} expired module{stats.expired > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs sm:text-sm text-red-600">
                    Complete immediately
                  </p>
                </div>
              </div>
            )}
            {stats.due_soon > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4 flex items-start sm:items-center gap-2 sm:gap-3">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5 sm:mt-0" />
                <div>
                  <p className="text-sm sm:text-base font-medium text-amber-700">
                    {stats.due_soon} module{stats.due_soon > 1 ? 's' : ''} due within 30 days
                  </p>
                  <p className="text-xs sm:text-sm text-amber-600">
                    Renew to maintain compliance
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Training Modules Grid */}
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-[#2D4A4A] mb-3 sm:mb-4">Training Modules</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {modules.map((module) => {
              const config = statusConfig[module.status] || statusConfig.not_started;
              const StatusIcon = config.icon;

              return (
                <Link 
                  key={module.id} 
                  to={`/training/${module.id}`}
                  data-testid={`module-card-${module.id}`}
                >
                  <Card className="module-card border border-[#A8E6CF]/30 h-full overflow-hidden bg-white hover:border-[#5BA4A4]/50">
                    {module.image_url && (
                      <div className="h-28 sm:h-40 overflow-hidden">
                        <img 
                          src={module.image_url} 
                          alt={module.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start justify-between mb-1 sm:mb-2">
                        <Badge 
                          variant="outline" 
                          className={`${config.color} text-[10px] sm:text-xs`}
                        >
                          <StatusIcon className={`w-2.5 h-2.5 sm:w-3 sm:h-3 mr-1 ${config.iconColor}`} />
                          {config.label}
                        </Badge>
                        {module.quiz_score !== null && module.quiz_score !== undefined && (
                          <span className="text-xs sm:text-sm font-medium text-[#5A7A7A]">
                            {module.quiz_score}%
                          </span>
                        )}
                      </div>
                      <h4 className="font-semibold text-sm sm:text-base text-[#2D4A4A] mb-1">{module.title}</h4>
                      <p className="text-xs sm:text-sm text-[#5A7A7A] line-clamp-2 mb-2 sm:mb-3">
                        {module.description}
                      </p>
                      {module.expires_at && (
                        <p className={`text-[10px] sm:text-xs ${
                          module.status === 'expired' ? 'text-red-500' : 
                          module.status === 'due_soon' ? 'text-amber-600' : 'text-[#5A7A7A]'
                        }`}>
                          {module.status === 'expired' ? 'Expired: ' : 'Expires: '}
                          {new Date(module.expires_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      )}
                      <div className="flex items-center justify-end mt-2 sm:mt-3 text-[#5BA4A4]">
                        <span className="text-xs sm:text-sm font-medium">
                          {module.status === 'completed' || module.status === 'due_soon' 
                            ? 'Certificate' 
                            : module.status === 'in_progress' 
                            ? 'Continue' 
                            : 'Start'}
                        </span>
                        <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StaffDashboard;
